import type { ReactNode } from "react"

/** Which page a step plays on — plan scope for the Impact Report tour,
 *  business scope for the Customer Hub tour. Each tour controller supplies
 *  path building and matching for the kinds its sections use. */
export type TourPage =
  | { kind: "impact_report" }
  | { kind: "result"; slug: string }
  | { kind: "pre_plan_questions" }
  | { kind: "marketing_plan" }
  | { kind: "checkout" }
  | { kind: "business_inbox" }
  | { kind: "business_inbox_conversation" }
  | { kind: "business_leads" }
  | { kind: "business_lead_detail" }
  | { kind: "business_reporting" }
  | { kind: "agency_console" }
  | { kind: "agency_pricing_groups" }

export interface TourStep {
  page: TourPage
  /** data-tour anchor the step highlights. */
  target: string
  title: string
  /** Function bodies receive the current Inertia page props so copy can quote
   *  the analysis's real numbers; they must return a fallback sentence when
   *  the data is missing. */
  body: ReactNode | ((props: Record<string, unknown>) => ReactNode)
  /** Primary button label; defaulted by the engine when omitted. */
  btn?: string
  /** "click" steps advance when the visitor clicks the highlighted element. */
  action?: "click"
  /** Narrows which element inside `target` advances a "click" step; the rest of
   *  the spotlight stays clickable without moving the tour on. */
  advanceOn?: string
  /** Where the tooltip goes. Below the target by default (above when it
   *  doesn't fit); "above" tries above first, for a step whose copy points
   *  at what sits just under the target. "side" goes beside the target, for
   *  steps where text above or below it must stay readable. */
  placement?: "above" | "side"
  /** Muted italic footnote under the body. */
  note?: string
  /** Replaces the generic "Click the highlighted element" hint on click steps. */
  hint?: string
  /** Optional steps are skipped when their anchor never renders (e.g. the
   *  Talk-to-a-Human trigger on agencies that haven't configured it) —
   *  the Product Fruits flows expressed this as a condition branch. */
  optional?: boolean
  /** Floating steps render as a centered modal card over a full-viewport dim
   *  — no spotlight (the welcome-popup style). `target` is ignored. */
  floating?: boolean
}

export interface TourSection {
  id: string
  label: string
  steps: TourStep[]
}

const impactReport: TourPage = { kind: "impact_report" }
const result = (slug: string): TourPage => ({ kind: "result", slug })
const prePlanQuestions: TourPage = { kind: "pre_plan_questions" }
const marketingPlan: TourPage = { kind: "marketing_plan" }

const dig = (obj: unknown, ...path: string[]): unknown =>
  path.reduce<unknown>(
    (o, key) =>
      o && typeof o === "object"
        ? (o as Record<string, unknown>)[key]
        : undefined,
    obj,
  )

const resultImpact = (props: Record<string, unknown>, ...path: string[]) =>
  dig(
    props,
    "analysis_result",
    "performance_raw_data",
    "expected_impact",
    ...path,
  )

/** "4H" → "4 hours", "5M" → "5 minutes"; unknown formats pass through. */
const formatResponseTime = (value: unknown): string | null => {
  if (typeof value !== "string" || !value.trim()) return null
  const match = /^(\d+)\s*([HM])$/i.exec(value.trim())
  if (!match) return value
  const n = Number(match[1])
  const unit = match[2].toUpperCase() === "H" ? "hour" : "minute"
  return `${n} ${unit}${n === 1 ? "" : "s"}`
}

const asCount = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : null

/** Tour copy replicated from the approved mockup (itself ported from the
 *  production GB Flow V2 Product Fruits flows). */
export const TOUR_SECTIONS: TourSection[] = [
  {
    id: "overview",
    label: "Impact Report Overview",
    steps: [
      {
        page: impactReport,
        target: "ir-title",
        title: "📊 Meet the Impact Report",
        body: "This report is your secret weapon for closing deals! It takes everything you learned during the discovery chat and turns it into a professional business analysis that highlights exactly where your prospect needs help.",
        btn: "Show me more",
      },
      {
        page: impactReport,
        target: "ir-narrative",
        title: "💰 The Bottom Line",
        body: "Check out these projections to see the potential revenue waiting for your prospect. These numbers are a powerful way to show a business owner the real-world value of fixing their marketing gaps with your agency.",
        btn: "Let’s see the gaps",
      },
      {
        page: impactReport,
        target: "ir-snapshot",
        title: "🔍 Score Your Success",
        body: "This snapshot gives you a bird’s-eye view of how the business is performing across different categories. Use these scores to identify which services will have the biggest impact on their growth right away.",
        btn: "View the data",
      },
      {
        page: impactReport,
        target: "ir-card-speed_to_lead",
        action: "click",
        title: "🚀 Dive Deeper",
        body: (
          <>
            Let’s examine the business performance across different channels,
            starting with Speed to Lead. <b>Click the card to proceed.</b>
          </>
        ),
      },
    ],
  },
  {
    id: "speed",
    label: "Speed To Lead",
    steps: [
      {
        page: result("speed_to_lead"),
        target: "result-narrative",
        title: "⏱️ Faster response, more clients",
        body: (props) => {
          const current = formatResponseTime(
            resultImpact(props, "call_leads", "current_response_time"),
          )
          const optimized = formatResponseTime(
            resultImpact(props, "call_leads", "optimized_response_time"),
          )
          const leads = asCount(
            resultImpact(props, "monthly_metrics", "added_leads"),
          )
          const clients = asCount(
            resultImpact(props, "monthly_metrics", "new_clients"),
          )
          if (current && optimized && leads && clients) {
            return `The business currently responds to calls in ${current}. At ${optimized}, the same lead flow yields about ${leads} more leads and ${clients} more clients a month.`
          }
          return "Cutting the response time to just a few minutes recovers the extra leads and clients shown here."
        },
        btn: "Continue",
      },
      {
        page: result("speed_to_lead"),
        target: "result-impact",
        title: "📊 Visualize your growth",
        body: (props) => {
          const formGain = asCount(
            resultImpact(props, "form_leads", "potential_impact_percentage"),
          )
          const callGain = asCount(
            resultImpact(props, "call_leads", "potential_impact_percentage"),
          )
          const clients = asCount(
            resultImpact(props, "monthly_metrics", "new_clients"),
          )
          if (formGain && callGain && clients) {
            return `Implementing our Speed to Lead optimization services, the business will achieve a ${formGain}% boost in form conversions and a ${callGain}% increase in calls, leading to an additional +${clients} new clients per month.`
          }
          return "Implementing our Speed to Lead optimization services boosts form and call conversions, adding the new clients per month shown here."
        },
        btn: "Show me more",
      },
      {
        page: result("speed_to_lead"),
        target: "result-all-results",
        action: "click",
        title: "🏁 Ready for more?",
        body: (
          <>
            Let’s proceed to view additional channels the marketing gap analysis
            covers and their impact. <b>Click “All results”.</b>
          </>
        ),
      },
    ],
  },
  {
    id: "reviews",
    label: "Review Management",
    steps: [
      {
        page: impactReport,
        target: "ir-card-review_generation",
        action: "click",
        title: "🔍 Reputation Snapshot",
        body: (
          <>
            Start by examining the review performance. This specific card shows
            the current reputation score compared to the industry standard.{" "}
            <b>Click on the card</b> to dive deep into the details of how
            reviews affect the business’s bottom line.
          </>
        ),
      },
      {
        page: result("review_generation"),
        target: "result-narrative",
        title: "📊 Mind the Gap",
        body: (props) => {
          const raw = dig(props, "analysis_result", "performance_raw_data")
          const rating =
            dig(
              raw,
              "summary",
              "expected_impact",
              "rating_strategy",
              "current_rating",
            ) ??
            dig(raw, "expected_impact", "rating_strategy", "current_rating")
          const ratingText =
            typeof rating === "number" && rating > 0
              ? `current ${rating}-star rating`
              : "current star rating"
          return `This section reveals the distance between the ${ratingText} and the market leader. You can see exactly how many reviews are needed to compete at the highest level and boost the conversion rates.`
        },
        btn: "Continue",
      },
      {
        page: result("review_generation"),
        target: "result-profile-ctr",
        title: "✨ Growth Opportunity",
        body: "By implementing a review management service, we will be unlocking a massive visibility lift. The system projects a significant increase in monthly leads once we close the gap with the top competitors.",
        btn: "Tell me more",
      },
      {
        page: result("review_generation"),
        target: "result-location",
        title: "📍 Regional Focus",
        body: "Use this selector to toggle between different business locations and view how each location performs.",
        btn: "Got it",
      },
      {
        page: result("review_generation"),
        target: "result-new-growth",
        title: "📈 Impact Roadmap",
        body: "This breakdown illustrates the transition from the current standing to becoming a local leader. It calculates the specific gain in leads and clients we can expect per thousand monthly views.",
        btn: "Keep moving",
      },
      {
        page: result("review_generation"),
        target: "result-all-results",
        action: "click",
        title: "🎯 Review Complete",
        body: (
          <>
            You have successfully navigated the review analysis!{" "}
            <b>Use this link</b> to return to the full dashboard and explore
            other critical areas.
          </>
        ),
      },
    ],
  },
  {
    id: "nearme",
    label: "Near Me SEO",
    steps: [
      {
        page: impactReport,
        target: "ir-card-near_me_seo",
        action: "click",
        title: "🔍 Dive into the data",
        body: (
          <>
            Next we examine the Near Me SEO performance of the business.{" "}
            <b>Click the card to proceed.</b>
          </>
        ),
      },
      {
        page: result("near_me_seo"),
        target: "result-impact",
        title: "💰 Business impact",
        body: "Near Me SEO is crucial for local businesses. Improving the local map pack rankings can add new leads every month and lift the business above competitors in the area.",
        btn: "Keep going",
      },
      {
        page: result("near_me_seo"),
        target: "result-product",
        title: "📍 Map out the win",
        body: "Check out these specific keywords where the business is currently invisible. The grid showcases how the business ranks when searching for their services in their area. Anything above 3 is not visible in the map pack.",
        btn: "Almost there",
      },
      {
        page: result("near_me_seo"),
        target: "result-all-results",
        action: "click",
        title: "🚀 Next Steps",
        body: (
          <>
            Now that you know how to navigate these insights, let’s proceed to
            view the business overall data and detailed growth plan.{" "}
            <b>Click “All results”.</b>
          </>
        ),
      },
    ],
  },
  {
    id: "bizdata",
    label: "Business Data",
    steps: [
      {
        page: impactReport,
        target: "ir-business-data",
        title: "📊 The inputs behind the numbers",
        body: "Every projection builds on these six values, taken from the discovery chat and industry data.",
        btn: "Show me more",
      },
      {
        page: impactReport,
        target: "ir-business-data-edit",
        title: "✏️ Tailor the numbers",
        body: "Every business is unique. If needed, use this option to adjust the underlying data, like average ticket value or conversion rates, to make the financial projections as accurate as possible for the business.",
        btn: "Got it",
      },
      {
        page: impactReport,
        target: "ir-growth-plan",
        title: "🚀 Ready for takeoff?",
        body: "Once you have reviewed the snapshot and verified the inputs, this button leads into the full growth strategy. It is time to see exactly how we will help them scale!",
        btn: "Finish part ✓",
      },
    ],
  },
  // The last two sections are ported from the production Product Fruits flows
  // ("Lead count page" and "Growthplan") — the mockup only approximated them
  // on the Impact Report.
  {
    id: "leadacq",
    label: "Current Lead Acquisition",
    steps: [
      {
        page: prePlanQuestions,
        target: "ppq-card",
        title: "🛠️ Fine-tune the forecast",
        body: "Before we dive into the data, one final tweak to ensure the growth plan we build together is as accurate as possible for the business.",
        btn: "Show me how",
      },
      // One step per question. The demo's answers are locked, so each step
      // just has the visitor click the form's own Next.
      {
        page: prePlanQuestions,
        target: "ppq-crm",
        action: "click",
        advanceOn: "ppq-next",
        placement: "side",
        hint: "Click Next on the form",
        title: "📊 Input the vitals",
        body: (props) => {
          const answers = props.answers as { has_crm?: boolean } | undefined
          return (
            <>
              Whether the business uses a CRM shapes how leads are captured and
              followed up. For this demo it&rsquo;s set to{" "}
              <b>{answers?.has_crm ? "Yes" : "No"}</b>, so click <b>Next</b>.
            </>
          )
        },
      },
      {
        page: prePlanQuestions,
        target: "ppq-leads",
        action: "click",
        advanceOn: "ppq-submit",
        placement: "side",
        hint: "Click Next on the form",
        title: "📈 Continue with the plan",
        body: (
          <>
            The current monthly lead count sets the starting point for the
            forecast. It&rsquo;s filled in for the demo, so click <b>Next</b> to
            generate the full business growth plan.
          </>
        ),
      },
    ],
  },
  {
    id: "growthplan",
    label: "Growth Plan Breakdown",
    steps: [
      {
        page: marketingPlan,
        target: "plan-hero",
        title: "📈 High-level wins",
        body: "This top section gives you a bird's-eye view of the projected success. You can see the expected revenue and ROI for the first year, which helps set big-picture expectations right away.",
        btn: "Show me more",
      },
      {
        page: marketingPlan,
        target: "plan-switcher",
        title: "🎯 Speed to goal",
        body: "Use this selector to adjust the pace of the plan. It shows how changing the budget or strategy speed impacts the monthly goal and the time it takes to reach it.",
        btn: "Got it",
      },
      {
        page: marketingPlan,
        target: "plan-overview",
        title: "📝 The big picture",
        body: "This overview explains the 'why' behind the strategy. It details how the plan will dominate local visibility and ensure the website is ready to convert every single lead that hits the page.",
        btn: "Tell me more",
      },
      {
        page: marketingPlan,
        target: "plan-month-detail-1",
        title: "🛠️ Launching",
        body: "Every plan starts with a strong foundation. Month 1 is dedicated to establishing the digital infrastructure, ensuring that all the tracking and conversion systems are live before scaling up.",
        btn: "Moving on",
      },
      {
        page: marketingPlan,
        target: "plan-month-toggle-3",
        action: "click",
        title: "📍 Action Plan & Execution",
        body: (
          <>
            Each month has a step-by-step breakdown of the specific tasks and
            deliverables planned, giving you full visibility into the execution.{" "}
            <b>Click Month 3</b> to see the plan once it&rsquo;s in full swing.
          </>
        ),
      },
      {
        page: marketingPlan,
        target: "plan-month-budget-3",
        title: "🗓️ Budget",
        body: "A complete breakdown of your total monthly budget and individual service costs for this phase.",
        btn: "What's inside?",
      },
      {
        page: marketingPlan,
        target: "plan-month-detail-3",
        title: "🔍 Deep dive",
        body: "Want to see the nitty-gritty? You can expand these items to see exactly what each service involves and how it directly contributes to the growth plan.",
        btn: "Almost there",
      },
      {
        page: marketingPlan,
        target: "talk-to-human",
        optional: true,
        title: "🤝 Get expert advice",
        body: (
          <>
            Enabling the &lsquo;Talk to a Human&rsquo; button displays your
            phone number and booking link to prospects. Enter your own contact
            details, or select <b>Sales Done-For-You</b> to let our team answer
            questions and close deals for you.
          </>
        ),
        btn: "Keep moving",
      },
      {
        page: marketingPlan,
        target: "agency-console",
        optional: true,
        title: "🗓️ Further tweaking",
        body: "Use the Agency Console to customize your client's plan: add or remove products, adjust pricing, and fix any details entered incorrectly during the chat discovery. Need to tailor the growth plan based on a prospect's request? You have total flexibility to tweak every detail before finalizing.",
        btn: "Final look",
      },
      {
        page: marketingPlan,
        target: "plan-checkout",
        floating: true,
        title: "✅ Ready to grow",
        body: "The growth plan looks great! Executing it turns the proposal into an active growth engine: the work begins and the client is billed for the first month.",
        btn: "Let's execute it",
      },
      {
        page: marketingPlan,
        target: "plan-checkout",
        action: "click",
        title: "🚀 Start execution",
        body: (
          <>
            <b>Click Start execution</b> to move to checkout, where the client
            confirms the plan and pays to set it live.
          </>
        ),
      },
    ],
  },
]

export type TourSectionStatus = "pending" | "active" | "done" | "skipped"
